import { App, VersionStatus } from '~/swagger/portal/Api';
import { allowNonProdDiagnostics } from '~/utils/env';
import type { ClientVersionInfoDto } from '~/swagger/portal/Api';

export const MOCK_STORAGE_KEY = 'maintenance_mock_mode';

export enum MockMode {
  NORMAL = 'normal',
  MAINTENANCE = 'maintenance',
  SERVER_DOWN = 'server_down',
  SLOW_RESPONSE = 'slow_response',
  INVALID_RESPONSE = 'invalid_response',
}

export const mockResponses: Record<MockMode, () => Promise<ClientVersionInfoDto[]>> = {
  [MockMode.NORMAL]: async () => [
    {
      app: App.CompanyApp,
      platform: 'web' as any,
      appVersion: '1.0.0',
      status: VersionStatus.Ok,
    },
  ],

  [MockMode.MAINTENANCE]: async () => [
    {
      app: App.CompanyApp,
      platform: 'web' as any,
      appVersion: '1.0.0',
      status: VersionStatus.Maintenance,
    },
  ],

  [MockMode.SERVER_DOWN]: async () => {
    throw new Error('Network error: Server unreachable');
  },

  [MockMode.SLOW_RESPONSE]: async () => {
    await new Promise((resolve) => setTimeout(resolve, 30000));
    return [
      {
        app: App.CompanyApp,
        platform: 'web' as any,
        appVersion: '1.0.0',
        status: VersionStatus.Ok,
      },
    ];
  },

  [MockMode.INVALID_RESPONSE]: async () => {
    return [] as any;
  },
};

const notifyMaintenanceMock = (...args: unknown[]) => {
  console.warn(...args);
};

export const setMockMode = (mode: MockMode | null) => {
  if (mode === null) {
    localStorage.removeItem(MOCK_STORAGE_KEY);
    notifyMaintenanceMock('✅ Mock mode disabled');
  } else {
    localStorage.setItem(MOCK_STORAGE_KEY, mode);
    notifyMaintenanceMock(`✅ Mock mode set to: ${mode}`);
  }
  notifyMaintenanceMock('🔄 Reload page to apply changes');
};

export const getMockMode = (): MockMode | null => {
  const mode = localStorage.getItem(MOCK_STORAGE_KEY);
  return mode as MockMode | null;
};

export const getMockResponse = async (): Promise<ClientVersionInfoDto[]> => {
  const mode = getMockMode();
  if (!mode || !mockResponses[mode]) {
    throw new Error('Invalid mock mode');
  }
  return mockResponses[mode]();
};

if (allowNonProdDiagnostics()) {
  (window as any).MaintenanceMock = {
    enable: (mode: MockMode) => setMockMode(mode),
    disable: () => setMockMode(null),
    modes: MockMode,
    currentMode: () => getMockMode(),
    help: () => {
      notifyMaintenanceMock(`
🔧 Maintenance Mock Helper
========================

Enable mock mode:
  MaintenanceMock.enable(MaintenanceMock.modes.MAINTENANCE)
  MaintenanceMock.enable(MaintenanceMock.modes.SERVER_DOWN)
  MaintenanceMock.enable(MaintenanceMock.modes.NORMAL)

Disable mock mode:
  MaintenanceMock.disable()

Check current mode:
  MaintenanceMock.currentMode()

Available modes:
  - NORMAL: Normal operation (status: "ok")
  - MAINTENANCE: Maintenance mode (status: "maintenance")
  - SERVER_DOWN: Simulates server error
  - SLOW_RESPONSE: 30 second delay
  - INVALID_RESPONSE: Empty response

After changing mode, reload the page!
      `);
    },
  };

  notifyMaintenanceMock('🔧 Maintenance Mock available. Type MaintenanceMock.help() for usage');
}
